/* ============================================================================
   ClassPerformanceChart / styles.js
   Card compacto e colapsável, alinhado ao design system da página da turma
   (teal #003e4f, cards brancos, borda var(--line), cantos suaves).
   Fica ANTES dos botões de funções principais, então ocupa pouca altura:
   colapsado é só o cabeçalho; aberto, um gráfico baixo (~180px) + legenda.
   ========================================================================== */

   import styled from 'styled-components'

   const TEAL = 'var(--teal)'
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
   const TEAL_LIGHT = 'var(--teal-light)'
   const GRAY_LINE = 'var(--line)'
   const GRAY_TEXT = 'var(--text-muted)'
   
   const bp = { tablet: '768px', mobile: '540px', small: '400px' }
   
   export const Wrapper = styled.section`
     width: 100%;
     box-sizing: border-box;
     background: var(--surface);
     border: 1px solid ${GRAY_LINE};
     border-radius: 14px;
     overflow: hidden;
   `
   
   export const Header = styled.button`
     width: 100%;
     display: flex;
     align-items: center;
     justify-content: space-between;
     gap: 12px;
     padding: 12px 16px;
     background: var(--surface);
     border: none;
     cursor: pointer;
     text-align: left;
     color: ${TEAL_TEXT};
     transition: background .15s ease;
   
     &:hover { background: var(--surface); }
     &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: -2px; }
   
     @media (max-width: ${bp.mobile}) { padding: 10px 12px; }
   `
   
   export const HeaderLeft = styled.div`
     display: flex;
     align-items: center;
     gap: 10px;
     min-width: 0;
   
     svg { color: ${TEAL_TEXT}; flex-shrink: 0; }
   `
   
   export const Title = styled.div`
     font-size: 14px;
     font-weight: 700;
     color: ${TEAL_TEXT};
     line-height: 1.2;
   `
   
   export const Subtitle = styled.div`
     font-size: 11.5px;
     color: ${GRAY_TEXT};
     line-height: 1.2;
     margin-top: 1px;
   
     @media (max-width: ${bp.mobile}) { font-size: 11px; }
   `
   
   export const Toggle = styled.span`
     display: inline-flex;
     color: ${GRAY_TEXT};
     transition: transform .2s ease;
     transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
     flex-shrink: 0;
   `
   
   export const Body = styled.div`
     padding: 8px 12px 12px;
     border-top: 1px solid ${GRAY_LINE};
   
     @media (max-width: ${bp.mobile}) { padding: 8px 8px 10px; }
   `
   
   /* alternador de visões: Comparar | Evolução (+ badge de status à direita) */
   export const ViewSwitch = styled.div`
     display: flex;
     align-items: center;
     gap: 6px;
     margin: 2px 0 6px;
     flex-wrap: wrap;
   `
   
   /* grupo dos botões (segmented control) */
   export const SwitchGroup = styled.div`
     display: inline-flex;
     gap: 2px;
     padding: 3px;
     background: #f0f4f6;
     border: 1px solid ${GRAY_LINE};
     border-radius: 10px;
   `
   
   export const SwitchButton = styled.button`
     display: inline-flex;
     align-items: center;
     gap: 5px;
     padding: 5px 12px;
     border: none;
     border-radius: 8px;
     font-size: 12.5px;
     font-weight: 600;
     cursor: pointer;
     transition: background .15s ease, color .15s ease, box-shadow .15s ease;
   
     color: ${({ $active }) => ($active ? TEAL : GRAY_TEXT)};
     background: ${({ $active }) => ($active ? 'var(--surface)' : 'transparent')};
     box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,.10)' : 'none')};
   
     svg { flex-shrink: 0; }
     &:hover { color: ${TEAL_TEXT}; }
     &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 1px; }
   
     @media (max-width: ${bp.mobile}) { padding: 5px 10px; font-size: 12px; }
   `
   
   /* rolagem horizontal do gráfico quando há muitas matérias (essencial no mobile).
      O gráfico recebe largura mínima proporcional; em telas estreitas o usuário
      desliza lateralmente em vez de espremer tudo num espaço ilegível. */
   export const ChartScroll = styled.div`
     width: 100%;
     height: 100%;
     overflow-x: auto;
     overflow-y: hidden;
     -webkit-overflow-scrolling: touch;
   
     /* barra de rolagem discreta */
     scrollbar-width: thin;
     &::-webkit-scrollbar { height: 6px; }
     &::-webkit-scrollbar-thumb { background: ${GRAY_LINE}; border-radius: 3px; }
   `
   
   export const ChartInner = styled.div`
     height: 100%;
     /* largura mínima calculada por matéria (via prop $minWidth) */
     min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : '100%')};
     width: 100%;
   `
   
   export const ChartArea = styled.div`
     width: 100%;
     height: 220px;
     margin-top: 4px;
   
     /* no mobile o gráfico ganha altura (mais legível em tela estreita) */
     @media (max-width: ${bp.tablet}) { height: 230px; }
     @media (max-width: ${bp.mobile}) { height: 250px; }
     @media (max-width: ${bp.small})  { height: 260px; }
   `
   
   export const Legend = styled.div`
     display: flex;
     flex-wrap: wrap;
     gap: 6px 14px;
     margin-top: 10px;
     padding-top: 10px;
     border-top: 1px dashed ${GRAY_LINE};
   `
   
   export const LegendItem = styled.span`
     display: inline-flex;
     align-items: center;
     gap: 6px;
     font-size: 12px;
     color: ${GRAY_TEXT};
     line-height: 1.2;
   
     @media (max-width: ${bp.mobile}) { font-size: 11px; }
   `
   
   export const Swatch = styled.span`
     width: 11px;
     height: 11px;
     border-radius: 3px;
     flex-shrink: 0;
   `
   
   export const StateMsg = styled.div`
     padding: 22px 8px;
     text-align: center;
     color: ${GRAY_TEXT};
     font-size: 13px;
   `
   
   /* badge de status geral da turma (fica ao lado do switch de visões) */
   export const TrendBadge = styled.span`
     display: inline-flex;
     align-items: center;
     gap: 5px;
     margin-left: auto;
     align-self: center;
     padding: 4px 10px;
     border-radius: 999px;
     font-size: 12px;
     font-weight: 700;
     color: ${({ $color }) => $color};
     background: ${({ $color }) => `${$color}14`};
     border: 1px solid ${({ $color }) => `${$color}33`};
     white-space: nowrap;
   
     svg { flex-shrink: 0; }
   
     @media (max-width: ${bp.mobile}) {
       font-size: 11px;
       padding: 3px 8px;
     }
   `
   
   /* nota discreta na legenda (ex.: "até o 2º Bim") */
   export const LegendNote = styled.span`
     font-size: 11.5px;
     font-style: italic;
     color: ${GRAY_TEXT};
     align-self: center;
   
     @media (max-width: ${bp.mobile}) { font-size: 11px; }
   `
   
   /* dica exibida quando ainda não há período suficiente p/ status */
   export const TrendHint = styled.span`
     display: inline-flex;
     align-items: center;
     margin-left: auto;
     align-self: center;
     padding: 4px 10px;
     border-radius: 999px;
     font-size: 11.5px;
     font-weight: 600;
     color: ${GRAY_TEXT};
     background: #f0f4f6;
     border: 1px dashed ${GRAY_LINE};
     white-space: nowrap;
   
     @media (max-width: ${bp.mobile}) {
       font-size: 10.5px;
       padding: 3px 8px;
       white-space: normal;
       text-align: right;
     }
   `
   
   /* linha da legenda de evolução: matéria + selo de tendência */
   export const LegendRow = styled.div`
     display: inline-flex;
     align-items: center;
     gap: 8px;
   `
   
   /* mini-selo de tendência por matéria */
   export const MatterTrend = styled.span`
     display: inline-flex;
     align-items: center;
     gap: 3px;
     font-size: 11px;
     font-weight: 700;
     color: ${({ $color }) => $color};
   
     svg { flex-shrink: 0; }
   
     @media (max-width: ${bp.mobile}) { font-size: 10.5px; }
   `